import React, { useState, useEffect, useRef } from 'react';
import type { DriveOmfzFile } from '../../../utils/driveApi';
import type { JobLifecycleEntry } from '../../../utils/jobLifecycleStorage';
import {
  FINGERING_ERROR_LABEL,
  getFingeringErrorDetail,
} from '../../../constants/fingeringErrorMessages';
import { getHistoryLastSeen, setHistoryLastSeen } from '../../../utils/historyLastSeen';
import { PART_LABELS } from '../../../constants/fingering';
import type { FingeringPart } from '../../../types/fingering';
import { formatDuration } from '../../../utils/formatDuration';
import { useNow } from '../../../hooks/useNow';
import DriveHistoryRow from './DriveHistoryRow';

export interface DriveHistoryModalProps {
  isOpen: boolean;
  items: DriveOmfzFile[];
  isLoading: boolean;
  error: string | null;
  onSelect: (file: DriveOmfzFile) => void;
  onClose: () => void;
  /** issue #138: 履歴 item の削除。 指定時のみ各行に削除ボタンを描画する。 */
  onDelete?: (file: DriveOmfzFile) => void;
  /** issue #138: 削除処理中の fileId。 当該行のボタンを disable する。 */
  deletingId?: string | null;
  /** issue #76 Phase 3: 進行中ジョブ (queued / processing)。未指定は空配列扱い。 */
  activeJobs?: JobLifecycleEntry[];
  /** issue #76 Phase 3: 直近失敗ジョブ (error_processing / failed / expired)。未指定は空配列扱い。 */
  recentlyFailedJobs?: JobLifecycleEntry[];
  /** issue #95 (#28): 自動保存 ON の expired (Drive 保存済みの可能性・要確認)。未指定は空配列扱い。 */
  needsDriveCheckJobs?: JobLifecycleEntry[];
  /** issue #76 Phase 3: 失敗ジョブの「再投入」ボタン。 */
  onRetryJob?: (jobId: string) => void;
  /** issue #76 Phase 3: 失敗ジョブの「消す」ボタン (jobLifecycleStorage の dismiss と接続)。 */
  onDismissFailedJob?: (jobId: string) => void;
}

// issue #169: formatBytes/formatDate は driveHistoryFormat.ts へ抽出し DriveHistoryRow と共有。

// issue #76 Phase 5-β: ERROR_KIND_LABEL / describeError は fingeringErrorMessages.ts に切り出し済。
// 互換 helper: ラベルは FINGERING_ERROR_LABEL を一覧、 fallback は errorKind 生 → errorMessage → '失敗'。
function describeError(entry: JobLifecycleEntry): string {
  if (entry.errorKind && FINGERING_ERROR_LABEL[entry.errorKind]) {
    return FINGERING_ERROR_LABEL[entry.errorKind];
  }
  if (entry.errorKind) return entry.errorKind;
  if (entry.errorMessage) return entry.errorMessage;
  return '失敗';
}

/**
 * 履歴の表示名 (issue #91 round2 #41)。 part は生 (Guitar1) でなく PART_LABELS (1st-4th) に変換し、
 * mode で出し分ける:
 * - mode='part' (パート譜): part 表記を出さない (元々 1 パートなので冗長)。
 * - mode='score' (総譜): part を (1st-4th) で付与。
 * - mode 欠落 (古い entry): 安全側で PART_LABELS 変換のみ ((1st-4th) を付与、 Guitar は出さない)。
 * 未知 part (Guitar1-4 以外) のみ生文字列にフォールバックする。
 */
function entryDisplayName(entry: JobLifecycleEntry): string {
  const fileName = entry.params?.fileName ?? '(無題)';
  const part = entry.params?.part;
  const mode = entry.params?.mode;
  if (!part || mode === 'part') return fileName;
  const label = PART_LABELS[part as FingeringPart] ?? part;
  return `${fileName} (${label})`;
}

const DriveHistoryModal: React.FC<DriveHistoryModalProps> = ({
  isOpen,
  items,
  isLoading,
  error,
  onSelect,
  onClose,
  onDelete,
  deletingId,
  activeJobs,
  recentlyFailedJobs,
  needsDriveCheckJobs,
  onRetryJob,
  onDismissFailedJob,
}) => {
  // issue #76 Phase 5-β: 失敗 row クリックで詳細パネルを展開する。 最新 1 件のみ展開、 再クリックで閉じる。
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  // issue #84 要望3: 新着判定の基準時刻。 open 中は固定 (ちらつき回避)、 open した瞬間に取り込む。
  const [lastSeenSnapshot, setLastSeenSnapshot] = useState(0);
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // open 遷移: 判定基準を固定する。
      setLastSeenSnapshot(getHistoryLastSeen());
    } else if (!isOpen && prevIsOpenRef.current) {
      // close 遷移: 閲覧済みとして last-seen を更新する → 次回からマークが消える。
      setHistoryLastSeen();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const active = activeJobs ?? [];
  const failed = recentlyFailedJobs ?? [];
  const needsCheck = needsDriveCheckJobs ?? [];
  // issue #109: 進行中ジョブがある間だけ 1 本のクロックで経過をライブ更新する
  // (idle では interval を張らない)。hooks は早期 return より前で呼ぶ。
  const now = useNow(isOpen && active.length > 0);

  if (!isOpen) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Drive 履歴"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50">
      <div className="bg-white w-full md:w-96 max-h-[80vh] md:rounded-lg rounded-t-xl shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">📚 履歴</h2>
          <button type="button" aria-label="閉じる" onClick={onClose}
            className="text-2xl text-slate-500 hover:text-slate-800">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-3">
          {/* 進行中セクション (件数 0 で非表示) */}
          {active.length > 0 && (
            <section aria-labelledby="active-jobs-heading" className="mb-3">
              <h3 id="active-jobs-heading" className="text-sm font-bold text-slate-700 mb-2">
                進行中 ({active.length} 件)
              </h3>
              <ul className="space-y-1">
                {active.map((entry) => (
                  <li key={entry.jobId}
                    className="p-3 rounded-lg border border-slate-200 bg-blue-50">
                    <div className="font-medium text-slate-800">
                      🔄 {entryDisplayName(entry)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {/* issue #84 要望2 / #109: queued=「順番待ち」・processing=「処理中…」+ 経過 duration ライブ表示。 */}
                      {entry.status === 'queued' ? '順番待ち' : '処理中…'} {formatDuration(now - entry.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 直近失敗セクション (件数 0 で非表示) */}
          {failed.length > 0 && (
            <section aria-labelledby="failed-jobs-heading" className="mb-3">
              <h3 id="failed-jobs-heading" className="text-sm font-bold text-slate-700 mb-2">
                直近失敗 ({failed.length} 件)
              </h3>
              <ul className="space-y-1">
                {failed.map((entry) => {
                  const isExpanded = expandedJobId === entry.jobId;
                  const detail = getFingeringErrorDetail(entry.errorKind);
                  return (
                    <li key={entry.jobId}
                      className="p-3 rounded-lg border border-red-200 bg-red-50">
                      {/* row 全体を toggle ボタンに (aria-expanded で開閉状態を伝達) */}
                      <button
                        type="button"
                        onClick={() => setExpandedJobId(isExpanded ? null : entry.jobId)}
                        aria-expanded={isExpanded}
                        className="w-full text-left flex items-start gap-2"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-slate-800">
                            ❌ {entryDisplayName(entry)}
                          </div>
                          <div className="text-xs text-slate-600 mt-1">
                            {describeError(entry)}
                          </div>
                        </div>
                        <span className="text-slate-400 text-xs mt-0.5" aria-hidden="true">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </button>
                      {/* 展開時の詳細パネル */}
                      {isExpanded && (
                        <div className="mt-3 p-3 rounded-md bg-white border border-red-100 text-xs text-slate-700 space-y-2">
                          {detail ? (
                            <>
                              <p className="font-medium text-slate-800 whitespace-pre-line">
                                {detail.title}
                              </p>
                              {detail.cause && (
                                <p className="whitespace-pre-line">{detail.cause}</p>
                              )}
                              <div>
                                <p className="font-medium text-slate-700">おすすめの対処:</p>
                                <ul className="list-disc list-inside space-y-0.5 mt-1">
                                  {detail.actions.map((a, i) => (
                                    <li key={i}>{a}</li>
                                  ))}
                                </ul>
                              </div>
                              {entry.errorMessage && (
                                <p className="text-slate-500 break-words">
                                  詳細: {entry.errorMessage}
                                </p>
                              )}
                            </>
                          ) : (
                            // 未知 errorKind: detail なし、 errorMessage のみ表示
                            entry.errorMessage && (
                              <p className="text-slate-500 break-words">
                                {entry.errorMessage}
                              </p>
                            )
                          )}
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <button type="button"
                          onClick={() => onRetryJob?.(entry.jobId)}
                          className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600">
                          再投入
                        </button>
                        <button type="button"
                          onClick={() => onDismissFailedJob?.(entry.jobId)}
                          className="text-xs px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300">
                          消す
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* issue #95 (#28): 要確認セクション (件数 0 で非表示)。
              ON expired = 自動保存済みの可能性。 赤の「失敗」でなく中立 (amber) で Drive 履歴確認を促す。 */}
          {needsCheck.length > 0 && (
            <section aria-labelledby="needs-check-heading" className="mb-3">
              <h3 id="needs-check-heading" className="text-sm font-bold text-slate-700 mb-2">
                要確認 ({needsCheck.length} 件)
              </h3>
              <ul className="space-y-1">
                {needsCheck.map((entry) => (
                  <li key={entry.jobId}
                    className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="font-medium text-slate-800">
                      🔍 {entryDisplayName(entry)}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Drive に保存済みの可能性があります。 下の Drive 履歴をご確認ください。
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button type="button"
                        onClick={() => onDismissFailedJob?.(entry.jobId)}
                        className="text-xs px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300">
                        消す
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Drive 履歴セクション (既存) */}
          {isLoading && <p className="text-center text-slate-500 py-8">読み込み中…</p>}
          {error && <p className="text-center text-red-600 py-4">{error}</p>}
          {!isLoading && !error && items.length === 0 && active.length === 0 && failed.length === 0 && (
            <p className="text-center text-slate-500 py-8">履歴がありません</p>
          )}
          {!isLoading && !error && items.length > 0 && (
            <section aria-labelledby="drive-history-heading">
              {(active.length > 0 || failed.length > 0) && (
                <h3 id="drive-history-heading" className="text-sm font-bold text-slate-700 mb-2">
                  Drive 履歴 ({items.length} 件)
                </h3>
              )}
              {items.map((file) => (
                // issue #169: 行描画 (メタ表示 + ホバー/スワイプ削除露出) は DriveHistoryRow に集約。
                // issue #84 要望3: last-seen より新しい完了は「新着」マーク。閲覧 (close) でクリア。
                <DriveHistoryRow
                  key={file.id}
                  file={file}
                  isNew={new Date(file.modifiedTime).getTime() > lastSeenSnapshot}
                  isDeleting={deletingId === file.id}
                  onSelect={onSelect}
                  onDelete={onDelete}
                />
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriveHistoryModal;
